/**
 * @fileoverview Notification creation submit flow extracted from the form hook.
 * @module pages/notificationForm/notificationFormSubmit
 */
import { logger, sanitizeError } from "../../utils/logger.js";
import { toast } from "react-toastify";
import { createAndSendCustomNotification } from "../../logic/customNotification/index.js";
import { getApiErrorMessage } from "../../utils/apiErrorMessages.js";
import { canCreateNotification } from "./notificationFormValidation.js";
import {
  uploadNotificationImageIfNeeded,
  buildNotificationPayload,
} from "./notificationFormImageUpload.js";

const NOTIFICATION_API_ERROR_HANDLERS = {
  onError: "NOTIFICATION_FORM_ERROR",
  onCompaniesError: "NO_COMPANIES",
  onUsersError: "NO_USERS",
  onPhoneNumbersError: "NO_PHONE_NUMBERS",
};

/** Builds toast callbacks for notification API failures. */
export const buildNotificationApiCallbacks = (ctx) => ({
  navigate: ctx.navigate,
  onSuccess: () => ctx.navigate("/notificationCenter"),
  onError: (error) => {
    logger.error("Error creating notification: ", {
      err: sanitizeError(error),
    });
    ctx.toastId.current = NOTIFICATION_API_ERROR_HANDLERS.onError;
    toast.error(
      getApiErrorMessage({ errorType: NOTIFICATION_API_ERROR_HANDLERS.onError }),
      { toastId: ctx.toastId.current },
    );
  },
  onCompaniesError: () => {
    ctx.toastId.current = NOTIFICATION_API_ERROR_HANDLERS.onCompaniesError;
    toast.error(
      getApiErrorMessage({
        errorType: NOTIFICATION_API_ERROR_HANDLERS.onCompaniesError,
      }),
      { toastId: ctx.toastId.current },
    );
  },
  onUsersError: () => {
    ctx.toastId.current = NOTIFICATION_API_ERROR_HANDLERS.onUsersError;
    toast.error(
      getApiErrorMessage({
        errorType: NOTIFICATION_API_ERROR_HANDLERS.onUsersError,
      }),
      { toastId: ctx.toastId.current },
    );
  },
  onPhoneNumbersError: () => {
    ctx.toastId.current = NOTIFICATION_API_ERROR_HANDLERS.onPhoneNumbersError;
    toast.error(
      getApiErrorMessage({
        errorType: NOTIFICATION_API_ERROR_HANDLERS.onPhoneNumbersError,
      }),
      { toastId: ctx.toastId.current },
    );
  },
});

/** Validates, uploads image if needed, and creates the notification. */
export const submitNotification = async (ctx) => {
  if (!canCreateNotification(ctx.formData, ctx.imageFile)) {
    ctx.setShowError(true);
    return;
  }
  ctx.setShowError(false);

  try {
    const uploadResult = await uploadNotificationImageIfNeeded({
      formData: ctx.formData,
      imageFile: ctx.imageFile,
      newObjectId: ctx.newObjectId,
      setNewObjectId: ctx.setNewObjectId,
      pendingUploadedImageRef: ctx.pendingUploadedImageRef,
      navigate: ctx.navigate,
    });

    if (!uploadResult.success) {
      ctx.setError(uploadResult.error);
      return;
    }

    const notificationData = buildNotificationPayload(
      ctx.formData,
      uploadResult.imagePayload,
    );
    if (!notificationData) {
      ctx.setShowError(true);
      return;
    }

    const result = await createAndSendCustomNotification(
      notificationData,
      buildNotificationApiCallbacks(ctx),
    );

    if (result?.success) {
      ctx.pendingUploadedImageRef.current = null;
      ctx.setNewObjectId(null);
    }
  } catch (error) {
    logger.error("Error uploading image: ", { err: sanitizeError(error) });
  }
};
